import { useId } from 'react';
import { Checkbox, Input, Label, Select } from '@shared/components';
import { SERVICE_HISTORY_DETAIL_LABEL, SERVICE_HISTORY_SOURCE_LABEL } from '../../../utils/cancellationCopy';
import { SERVICE_HISTORY_SOURCES, type ServiceHistorySource } from '../../../types/cancellation.types';

interface Props {
    confirmed: boolean;
    source: string;
    detail: string;
    errors: { confirmed?: string; source?: string; detail?: string };
    disabled: boolean;
    onConfirmedChange: (confirmed: boolean) => void;
    onSourceChange: (source: string) => void;
    onDetailChange: (detail: string) => void;
}

const SOURCE_OPTIONS = SERVICE_HISTORY_SOURCES.map((value) => ({ value, label: SERVICE_HISTORY_SOURCE_LABEL[value] }));

const isSource = (value: string): value is ServiceHistorySource => (SERVICE_HISTORY_SOURCES as readonly string[]).includes(value);

export function ServiceHistoryFields({ confirmed, source, detail, errors, disabled, onConfirmedChange, onSourceChange, onDetailChange }: Props) {
    const confirmId = useId();
    const sourceId = useId();
    const detailId = useId();

    return (
        <fieldset className="space-y-4 rounded-lg border border-mp-border p-4" disabled={disabled}>
            <legend className="px-1 text-sm font-semibold text-mp-foreground">Service history</legend>
            <div>
                <Checkbox
                    id={confirmId}
                    checked={confirmed}
                    onChange={onConfirmedChange}
                    disabled={disabled}
                    label="Service history has been confirmed and no servicing is left to invoice"
                    ariaDescribedBy={errors.confirmed ? `${confirmId}-error` : undefined}
                />
                {errors.confirmed && (
                    <p id={`${confirmId}-error`} className="mt-1 text-xs text-mp-error">
                        {errors.confirmed}
                    </p>
                )}
            </div>
            <div>
                <Label htmlFor={sourceId} required>
                    How was it confirmed
                </Label>
                <Select
                    id={sourceId}
                    options={SOURCE_OPTIONS}
                    placeholder="Select a source"
                    value={source}
                    onChange={onSourceChange}
                    disabled={disabled}
                    error={!!errors.source}
                    hint={errors.source}
                    ariaLabel="How the service history was confirmed"
                />
            </div>
            {isSource(source) && (
                <div>
                    <Label htmlFor={detailId} required={source !== 'thirty_day_rule'}>
                        {SERVICE_HISTORY_DETAIL_LABEL[source]}
                    </Label>
                    <Input
                        id={detailId}
                        value={detail}
                        onChange={(event) => onDetailChange(event.target.value)}
                        disabled={disabled}
                        error={!!errors.detail}
                        hint={errors.detail}
                        hintId={`${detailId}-error`}
                    />
                </div>
            )}
        </fieldset>
    );
}
